import React, { useState, useEffect, useCallback, useRef } from 'react';
import { etudiantsAPI } from '../services/api';
import { departementCourant, peutModifier } from '../services/droits';
import { analyserFeuille, analyserCollage, normaliserIntitule } from '../services/apogeeImport';

/**
 * MODULE ÉTUDIANTS — onglet 2, IMPORT APOGEE (lot 1.2)
 * ====================================================
 * Spec     : doc/modules/etudiants.md §0, §Onglet 2 section A, §Logique d'import
 * Maquette : doc/maquettes/etudiants_maquette_v5.html #tab-import
 * Moteur   : services/apogeeImport.js — appariement, contrôle de forme, liste blanche
 *
 * ── CE QUE LA MAQUETTE IMPOSE, ET QUE LA SPEC NE DIT PAS ─────────────────────
 * L'onglet est DEUX COLONNES ÉGALES côte à côte, APOGEE en bleu à gauche, SCODOC en
 * violet à droite, chacune défilant seule.
 *
 * ⚠️ Depuis le lot 1.4, ce composant ne rend QUE la colonne de gauche. Le cadre à
 * deux colonnes est remonté dans `Etudiants.js`, et la droite est `ImportScodoc.js`.
 * Au lot 1.2 les deux vivaient ici, avec une carte d'attente à droite — sans elle la
 * gauche aurait pris toute la largeur, et le lot 1.4 aurait dû la rétrécir.
 *
 * Viennent aussi de la maquette : la bande de dépôt COMPACTE (et non la grande zone
 * pointillée, qui est en CSS mais qu'elle n'instancie pas), le sélecteur d'année dans
 * la zone d'import, le bilan à trois compteurs avec sa date et son heure, et
 * l'historique dont les lignes sont cliquables.
 *
 * ── LE TROU DE LA MAQUETTE, ET COMMENT IL EST COMBLÉ ─────────────────────────
 * La maquette ne dessine AUCUNE prévisualisation, alors que le §0.8 en fait une
 * obligation : « rien ne s'écrit avant que l'utilisateur ait vu ce tableau ». Son
 * bouton s'appelle pourtant « Aperçu & importer ».
 *
 * Elle porte en revanche des règles CSS orphelines d'un dessin antérieur, jamais
 * instanciées : `.paste-preview`, `.paste-preview-header` (en-tête bleu #eff6ff sur
 * bordure #bfdbfe), `.paste-preview-table`, `.col-match` (pastille verte). La table
 * de prévisualisation a donc été dessinée puis retirée du HTML. Elle est reconstruite
 * ici AVEC CE VOCABULAIRE — reprendre la forme prévue plutôt que d'en inventer une.
 *
 * La prévisualisation se lit en deux temps, comme décidé le 2026-08-16 :
 *   · PAR COLONNE (§0.8) : libellé source, champ cible, taux de conformité, trois
 *     exemples. C'est là qu'on voit qu'une colonne est refusée, et pourquoi ;
 *   · PAR ÉTUDIANT (protocole commun d'import) : nouveaux, modifiés — avec les champs
 *     qui changent — et identiques. C'est ce comptage qui prouve, au second import,
 *     que rien ne double.
 *
 * ── CE LOT NE FAIT QUE LE MODE FICHIER ───────────────────────────────────────
 * Le mode « Coller (Ctrl+V) » est dessiné, présent, et désactivé avec sa raison : il
 * arrive au lot 1.2 bis. Ses pièges sont les siens — tout arrive en texte, la date
 * prend une troisième forme, il n'y a ni nom de fichier ni feuille à choisir — et ils
 * méritent leur propre recette.
 */

/** Extensions lues. `.csv` n'en fait pas partie : la scolarité envoie un classeur. */
const EXTENSIONS = ['xlsx', 'xlsm', 'xls'];

const messageErreur = (e, repli) => e?.response?.data?.error || e?.message || repli;

/** « 16/08/2026 10h32 » — la forme de l'en-tête de bilan de la maquette. */
function dateHeure(valeur) {
    if (!valeur) return '';
    const d = new Date(valeur);
    if (Number.isNaN(d.getTime())) return '';
    const p = (n) => String(n).padStart(2, '0');
    return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}h${p(d.getMinutes())}`;
}

const pourcent = (taux) => (taux === null || taux === undefined ? '—' : Math.round(taux * 100) + ' %');

/** Noms lisibles des champs, pour le tableau des différences. */
const NOMS_CHAMPS = {
    nom: 'Nom', prenom: 'Prénom', sexe: 'Sexe', date_naissance: 'Date de naissance',
    telephone: 'Portable', email: 'E-mail personnel', type_bac: 'Série du bac',
    mention_bac: 'Mention', annee_bac: 'Année du bac', cursus: 'Situation précédente',
    ine: 'INE', redoublant: 'Redoublant', boursier: 'Boursier',
    classement_admission: "Classement d'admission", formation: 'Formation',
    regime_inscription: "Régime d'inscription", inscription: "Inscription de l'année",
    email_institutionnel: 'E-mail institutionnel'
};
const nomChamp = (cle) => NOMS_CHAMPS[cle] || cle;

/** Au-delà de ce nombre, les numéros de ligne d'un même motif sont abrégés. */
const REPERES_MONTRES = 12;

/** Lignes non vides d'un collage — le compte annoncé sous la zone de saisie. */
function lignesCollees(texte) {
    return String(texte || '').split(/\r?\n/).filter((l) => l.trim() !== '').length;
}

/**
 * Les refus, regroupés PAR COUPLE quand le motif est un code non mappé.
 *
 * Trouvé à la relecture de l'image : sur le fichier réel, 77 lignes sont refusées
 * pour 13 codes, et le détail affichait « Le code B2ATNI/222 n'est associé à aucune
 * formation » dix fois d'affilée. Une liste qui répète dix fois la même phrase ne se
 * lit plus, et l'alerte du dessus donnait déjà le compte.
 *
 * Les numéros de ligne sont CONSERVÉS — la règle est qu'un échec remonte avec sa
 * ligne fautive — mais rassemblés sur une seule entrée. Les autres motifs restent
 * ligne à ligne : un INE contradictoire ou une clé absente est un cas individuel.
 */
function regrouperRefus(refuses) {
    const groupes = new Map();
    const seuls = [];

    for (const r of refuses || []) {
        if (r.motif !== 'code_non_mappe') {
            seuls.push({ reperes: 'L.' + r.ligne, message: r.message });
            continue;
        }
        const cle = `${r.code_etape}/${r.version_etape}`;
        if (!groupes.has(cle)) groupes.set(cle, { message: r.message, lignes: [] });
        groupes.get(cle).lignes.push(r.ligne);
    }

    const regroupes = [...groupes.entries()].map(([cle, g]) => {
        const montrees = g.lignes.slice(0, REPERES_MONTRES).map((l) => 'L.' + l).join(', ');
        const reste = g.lignes.length - REPERES_MONTRES;
        return {
            reperes: `${g.lignes.length} ligne${g.lignes.length > 1 ? 's' : ''}`,
            message: `${g.message} — ${montrees}${reste > 0 ? ` et ${reste} autre${reste > 1 ? 's' : ''}` : ''}`,
            cle
        };
    });

    return [...seuls, ...regroupes];
}

/** `true` / `false` / `null` ne se lisent pas à l'écran. */
function lisible(v) {
    if (v === null || v === undefined || v === '') return '—';
    if (v === true) return 'oui';
    if (v === false) return 'non';
    return String(v);
}

// ═════════════════════════════════════════════════════════════════════════════
// L'ONGLET
// ═════════════════════════════════════════════════════════════════════════════
function ImportApogee({ user, config, actif, anneeId, annees }) {
    const departementId = departementCourant(user, config);
    const modifiable = peutModifier(user, departementId, 'etudiants');

    const [dejaCharge, setDejaCharge] = useState(false);
    const [reglages, setReglages] = useState(null);
    const [nbMappings, setNbMappings] = useState(null);
    // Lot E.3 : l'étape ② associe un code SUR PLACE — il lui faut les groupes du département et les parcours de l'année
    const [formations, setFormations] = useState([]);
    const [parcours, setParcours] = useState([]);
    const [historique, setHistorique] = useState([]);

    // Étape : 'depot' → 'apercu' → 'bilan'
    const [etape, setEtape] = useState('depot');
    // Mode d'entrée des données. Le collage traverse le MÊME moteur que le fichier
    // (lot 1.2 bis) : seule la façon d'obtenir la matrice change.
    const [mode, setMode] = useState('fichier');
    const [colle, setColle] = useState('');
    const [occupe, setOccupe] = useState(false);
    const [erreur, setErreur] = useState(null);
    const [survol, setSurvol] = useState(false);

    // Le classeur lu, ses feuilles, et la feuille retenue.
    const classeur = useRef(null);
    const xlsx = useRef(null);
    const [fichierNom, setFichierNom] = useState(null);
    const [feuilles, setFeuilles] = useState([]);
    const [feuille, setFeuille] = useState('');

    const [analyse, setAnalyse] = useState(null);   // moteur local (colonnes, formes)
    const [apercu, setApercu] = useState(null);     // diff renvoyé par le serveur
    const [bilan, setBilan] = useState(null);       // résultat d'un import, ou le dernier

    const anneeActive = (annees || []).find((a) => a.id === anneeId) || null;
    const cleLiaison = reglages?.apogee_cle_liaison || 'Individu - Code Etudiant';

    // ── Chargement à la première ouverture de l'onglet ────────────────────────
    const charger = useCallback(async () => {
        if (!departementId) { setErreur('Aucun département sélectionné'); return; }
        try {
            const [param, imports] = await Promise.all([
                etudiantsAPI.getParametrage(departementId),
                etudiantsAPI.getImports(departementId, 'apogee')
            ]);
            setReglages(param.data.reglages);
            setNbMappings((param.data.mappings || []).length);
            setFormations(param.data.formations || []);
            setParcours(param.data.parcours || []);
            setHistorique(imports.data.imports || []);

            // La maquette affiche un bilan en permanence, daté : c'est le DERNIER
            // import, rechargé. Un écran vide au retour laisserait croire qu'aucun
            // import n'a jamais eu lieu.
            const dernier = (imports.data.imports || [])[0];
            if (dernier) setBilan(bilanDepuisJournal(dernier));
        } catch (e) {
            setErreur(messageErreur(e, "Impossible de lire l'état des imports"));
        }
    }, [departementId]);

    useEffect(() => {
        if (actif && !dejaCharge) { setDejaCharge(true); charger(); }
    }, [actif, dejaCharge, charger]);

    // ── Lecture du fichier ────────────────────────────────────────────────────
    const analyserClasseur = useCallback((nomFeuille) => {
        const XLSX = xlsx.current;
        const wb = classeur.current;
        if (!XLSX || !wb) return;
        const matrice = XLSX.utils.sheet_to_json(wb.Sheets[nomFeuille], {
            // `raw: true` est indispensable : la date de naissance est un serial Excel
            // — avec format sur 80 lignes, SANS format sur 67 — et le numéro étudiant
            // est numérique. Les laisser convertir en texte d'affichage perdrait la
            // seule forme exploitable (§0 et banc §5).
            header: 1, raw: true, defval: null, blankrows: true
        });
        setFeuille(nomFeuille);
        setAnalyse(analyserFeuille(matrice, cleLiaison));
        setApercu(null);
        setEtape('apercu');
    }, [cleLiaison]);

    const lireFichier = useCallback(async (fichier) => {
        setErreur(null);
        const ext = (fichier.name.split('.').pop() || '').toLowerCase();
        if (!EXTENSIONS.includes(ext)) {
            setErreur(`Format non supporté : « .${ext} ». Attendu un classeur ${EXTENSIONS.map((e) => '.' + e).join(', ')}`);
            return;
        }
        setOccupe(true);
        try {
            const XLSX = await import('xlsx');
            const donnees = new Uint8Array(await fichier.arrayBuffer());
            const wb = XLSX.read(donnees, { type: 'array' });
            xlsx.current = XLSX;
            classeur.current = wb;
            setFichierNom(fichier.name);
            setFeuilles(wb.SheetNames || []);
            setBilan(null);

            // Le classeur réel porte DEUX feuilles (`Liste_Scolarite` et
            // `ExportScodoc`). On retient celle qui contient la colonne clé ET des
            // données, plutôt que la première : ouvrir sur l'export SCODOC — ou sur une
            // feuille d'en-têtes vide — afficherait un refus alors que la bonne feuille
            // est juste à côté. La maquette ne prévoyait pas ce choix.
            const jaugee = (wb.SheetNames || []).map((nom) => {
                const m = XLSX.utils.sheet_to_json(wb.Sheets[nom], { header: 1, raw: true });
                const entetes = m[0] || [];
                const aLaCle = entetes.some(
                    (h) => normaliserIntitule(h) === normaliserIntitule(cleLiaison)
                );
                const nbDonnees = m.slice(1).filter(
                    (l) => (l || []).some((c) => String(c ?? '').trim() !== '')
                ).length;
                return { nom, aLaCle, nbDonnees };
            });
            const retenue = jaugee.find((f) => f.aLaCle && f.nbDonnees > 0)
                         || jaugee.find((f) => f.aLaCle)
                         || jaugee[0];
            analyserClasseur((retenue && retenue.nom) || wb.SheetNames[0]);
        } catch (e) {
            setErreur(messageErreur(e, 'Ce fichier n\'a pas pu être lu'));
        } finally {
            setOccupe(false);
        }
    }, [cleLiaison, analyserClasseur]);

    const surDepot = (e) => {
        e.preventDefault();
        setSurvol(false);
        if (!modifiable) return;
        const fichier = e.dataTransfer?.files?.[0];
        if (fichier) lireFichier(fichier);
    };

    // ── Prévisualisation côté serveur (le diff) ───────────────────────────────
    const demanderApercu = useCallback(async () => {
        if (!analyse || analyse.bloquant) return;
        setOccupe(true);
        setErreur(null);
        try {
            const { data } = await etudiantsAPI.apercuImportApogee(departementId, anneeId, analyse.lignes);
            setApercu(data);
        } catch (e) {
            setErreur(messageErreur(e, "L'aperçu a échoué"));
        } finally {
            setOccupe(false);
        }
    }, [analyse, departementId, anneeId]);

    // Le diff se demande dès que l'analyse locale est saine : c'est une lecture, elle
    // n'écrit rien, et l'utilisateur n'a pas à cliquer pour voir ce qu'il va faire.
    useEffect(() => {
        if (etape === 'apercu' && analyse && !analyse.bloquant && !apercu && !occupe) {
            demanderApercu();
        }
    }, [etape, analyse, apercu, occupe, demanderApercu]);

    // ── L'import ──────────────────────────────────────────────────────────────
    const importer = useCallback(async () => {
        if (!analyse || analyse.bloquant) return;
        setOccupe(true);
        setErreur(null);
        try {
            const { data } = await etudiantsAPI.importerApogee(
                // En mode coller il n'y a pas de fichier : le journal reçoit
                // `mode = 'coller'` et `fichier_nom = null`, et l'historique affiche
                // « collage » à la place du nom.
                departementId, anneeId, analyse.lignes,
                mode === 'coller' ? null : fichierNom, mode
            );
            setBilan(data);
            setEtape('bilan');
            const imports = await etudiantsAPI.getImports(departementId, 'apogee');
            setHistorique(imports.data.imports || []);
            const param = await etudiantsAPI.getParametrage(departementId);
            setNbMappings((param.data.mappings || []).length);
        } catch (e) {
            setErreur(messageErreur(e, "L'import a échoué"));
        } finally {
            setOccupe(false);
        }
    }, [analyse, departementId, anneeId, fichierNom, mode]);

    // ── Lot E.3 : l'étape ② — associer un code SUR PLACE (il rejoint le mapping du Paramétrage) ──────────────
    // Plus d'aller-retour vers l'onglet Paramétrage : la même route que lui, puis l'aperçu se recalcule (`setApercu(null)`
    // relance l'effet ci-dessus).
    const associerCode = useCallback(async (c, choix) => {
        setOccupe(true);
        setErreur(null);
        try {
            const { data } = await etudiantsAPI.creerMappings(departementId, choix.sous_niveau_id, [{
                code_etape: c.code_etape, version_etape: c.version_etape,
                regime_inscription: choix.regime_inscription, parcours_code: choix.parcours_code || null
            }]);
            if (data.refuses && data.refuses.length) setErreur(data.refuses[0].motif);
            setNbMappings((n) => (n || 0) + (data.crees || []).length);
        } catch (e) {
            setErreur(messageErreur(e, "Ce code n'a pas pu être associé"));
        } finally {
            setOccupe(false);
            setApercu(null);
        }
    }, [departementId]);

    /**
     * « Modifier » sur un code connu (demande de Bertrand, 2026-10-29) : une association faite par erreur se corrige ici,
     * sans quitter l'import — groupe, régime et parcours. Même route que le Paramétrage ; l'aperçu se recalcule.
     */
    const modifierCode = useCallback(async (c, choix) => {
        const m = c.mapping;
        setOccupe(true);
        setErreur(null);
        try {
            await etudiantsAPI.modifierMapping(m.id, departementId, {
                code_etape: c.code_etape, version_etape: c.version_etape, sous_niveau_id: choix.sous_niveau_id,
                regime_inscription: choix.regime_inscription, label: m.label || null, parcours_code: choix.parcours_code || null
            });
        } catch (e) {
            setErreur(messageErreur(e, "L'association n'a pas pu être modifiée"));
        } finally {
            setOccupe(false);
            setApercu(null);
        }
    }, [departementId]);

    /** Retour à la zone de saisie. Le texte collé est vidé aussi : le garder ferait
     *  réanalyser un collage que l'utilisateur croyait avoir abandonné. */
    const recommencer = useCallback(() => {
        classeur.current = null;
        setFichierNom(null);
        setFeuilles([]);
        setFeuille('');
        setAnalyse(null);
        setApercu(null);
        setErreur(null);
        setColle('');
        setEtape('depot');
    }, []);

    /** Bascule de mode. Elle remet tout à zéro : passer du fichier au collage en
     *  gardant l'analyse du fichier afficherait un aperçu qui ne correspond plus à
     *  ce que l'écran montre. */
    const changerMode = useCallback((nouveau) => {
        if (nouveau === mode) return;
        recommencer();
        setMode(nouveau);
    }, [mode, recommencer]);

    /** Analyse du texte collé. Même moteur que le fichier — seule la façon
     *  d'obtenir la matrice change (TSV du presse-papiers au lieu de SheetJS). */
    const analyserLeCollage = useCallback(() => {
        setErreur(null);
        setApercu(null);
        setAnalyse(analyserCollage(colle, cleLiaison));
        setEtape('apercu');
    }, [colle, cleLiaison]);

    // ═════════════════════════════════════════════════════════════════════════
    // RENDU
    // ═════════════════════════════════════════════════════════════════════════
    return (
        // ══════════ LA COLONNE GAUCHE, ET ELLE SEULE ══════════
        // Au lot 1.2 ce composant portait AUSSI le cadre à deux colonnes et la carte
        // d'attente de SCODOC : il fallait bien tenir la moitié droite pour que la
        // gauche ne prenne pas toute la largeur. Le lot 1.4 remplit cette moitié, et
        // le cadre remonte donc dans la coquille (`Etudiants.js`), qui porte déjà la
        // structure des quatre onglets. Un fichier nommé « ImportApogee » n'a pas à
        // décider de la mise en page de l'import SCODOC.
        <div className="etu-import-colonne">

            <div className="etu-carte">
                <div className="etu-carte-entete etu-entete-apogee">
                    <i className="fa-solid fa-file-import" />
                    <span>Import APOGEE</span>
                    <span className="etu-entete-note">Identité · Statut · Formation</span>
                </div>

                <div className="etu-import-corps">

                    {!modifiable && (
                        <div className="etu-alerte etu-alerte-info">
                            <i className="fa-solid fa-eye" />
                            <span>
                                Lecture seule : ton compte peut consulter ce module, pas y écrire.
                                L'historique et le dernier bilan restent visibles.
                            </span>
                        </div>
                    )}

                    {nbMappings === 0 && (
                        <div className="etu-alerte etu-alerte-warning">
                            <i className="fa-solid fa-triangle-exclamation" />
                            <span>
                                Aucun code APOGEE n'est associé à une formation : l'import
                                refuserait <strong>toutes</strong> les lignes du fichier.
                                À faire d'abord dans l'onglet <strong>Paramétrage</strong>.
                            </span>
                        </div>
                    )}

                    {erreur && (
                        <div className="etu-statut etu-statut-ko etu-statut-bloc">
                            <i className="fa-solid fa-circle-exclamation" />
                            <span>{erreur}</span>
                        </div>
                    )}

                    {/* ── Le sélecteur de mode ── */}
                    <div className="etu-modes">
                        <button
                            type="button"
                            className={'etu-mode' + (mode === 'fichier' ? ' active' : '')}
                            onClick={() => changerMode('fichier')}
                        >
                            <i className="fa-solid fa-file-excel" /> Fichier .xlsx
                        </button>
                        <button
                            type="button"
                            className={'etu-mode' + (mode === 'coller' ? ' active' : '')}
                            onClick={() => changerMode('coller')}
                        >
                            <i className="fa-solid fa-clipboard" /> Coller (Ctrl+V)
                        </button>
                    </div>

                    {/* ── Étape 1 bis : le collage (lot 1.2 bis) ──
                        Le guide en quatre étapes vient de la maquette. Il porte une
                        consigne que la maquette ne donnait pas et qui décide de tout :
                        SÉLECTIONNER AUSSI LA LIGNE DES EN-TÊTES. Sans elle, les
                        colonnes ne pourraient être identifiées que par leur position —
                        le devineur que le §0.8 interdit, et qui écrirait des données
                        fausses pour 46 % de l'effectif sur ce fichier à deux
                        extractions. */}
                    {etape === 'depot' && mode === 'coller' && (
                        <div className="etu-coller">
                            <div className="etu-coller-guide">
                                <span className="etu-coller-etape">
                                    <span className="etu-coller-num">1</span> Ouvrir le fichier dans Excel
                                </span>
                                <i className="fa-solid fa-chevron-right etu-coller-fleche" />
                                <span className="etu-coller-etape">
                                    <span className="etu-coller-num">2</span> Sélectionner
                                    <strong>&nbsp;les en-têtes&nbsp;</strong> et les lignes
                                </span>
                                <i className="fa-solid fa-chevron-right etu-coller-fleche" />
                                <span className="etu-coller-etape">
                                    <span className="etu-coller-num">3</span> <kbd>Ctrl+C</kbd>
                                </span>
                                <i className="fa-solid fa-chevron-right etu-coller-fleche" />
                                <span className="etu-coller-etape">
                                    <span className="etu-coller-num">4</span> <kbd>Ctrl+V</kbd> ici
                                </span>
                            </div>

                            <textarea
                                className="etu-coller-zone"
                                value={colle}
                                onChange={(e) => setColle(e.target.value)}
                                disabled={!modifiable || occupe}
                                placeholder={'Collez ici (Ctrl+V)…\n\nLa première ligne collée doit être celle des intitulés de colonnes.'}
                                spellCheck={false}
                            />

                            <div className="etu-coller-pied">
                                <span className="etu-aide">
                                    {colle.trim()
                                        ? `${lignesCollees(colle)} ligne${lignesCollees(colle) > 1 ? 's' : ''} collée${lignesCollees(colle) > 1 ? 's' : ''}, en-tête comprise`
                                        : 'Aucun contenu collé'}
                                </span>
                                <select
                                    value={anneeId || ''}
                                    disabled
                                    title="L'import porte toujours sur l'année active."
                                >
                                    <option value={anneeId || ''}>{anneeActive?.libelle || '—'}</option>
                                </select>
                                <button
                                    type="button"
                                    className="etu-btn etu-btn-apogee"
                                    disabled={!modifiable || occupe || !colle.trim()}
                                    onClick={analyserLeCollage}
                                >
                                    <i className="fa-solid fa-eye" /> Aperçu &amp; importer
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ── Étape 1 : le dépôt ── */}
                    {etape === 'depot' && mode === 'fichier' && (
                        <div
                            className={'etu-depot' + (survol ? ' survol' : '')}
                            onDragOver={(e) => { e.preventDefault(); setSurvol(true); }}
                            onDragLeave={() => setSurvol(false)}
                            onDrop={surDepot}
                        >
                            <i className="fa-solid fa-file-excel etu-depot-icone" />
                            <div className="etu-depot-texte">
                                Glissez votre <code>.xlsx</code> ici ou
                            </div>

                            {/* Le sélecteur d'année de la maquette, VERROUILLÉ sur
                                l'année active : décidé le 2026-08-16. Un import qui
                                pourrait viser une année passée réécrirait la formation
                                courante de l'étudiant. */}
                            <select
                                value={anneeId || ''}
                                disabled
                                title="L'import porte toujours sur l'année active. Pour en changer, il faut changer l'année active."
                            >
                                <option value={anneeId || ''}>{anneeActive?.libelle || '—'}</option>
                            </select>

                            <label className={'etu-btn etu-btn-apogee' + (modifiable ? '' : ' etu-btn-inerte')}>
                                <i className="fa-solid fa-upload" /> Choisir
                                <input
                                    type="file"
                                    accept={EXTENSIONS.map((e) => '.' + e).join(',')}
                                    disabled={!modifiable || occupe}
                                    onChange={(e) => {
                                        const f = e.target.files?.[0];
                                        // Remettre la valeur à zéro : sans cela, choisir
                                        // deux fois le même fichier ne déclenche rien.
                                        e.target.value = '';
                                        if (f) lireFichier(f);
                                    }}
                                />
                            </label>
                        </div>
                    )}

                    {/* ── Étape 2 : la prévisualisation ── */}
                    {etape === 'apercu' && analyse && (
                        <Previsualisation
                            analyse={analyse}
                            apercu={apercu}
                            mode={mode}
                            fichierNom={fichierNom}
                            feuilles={feuilles}
                            feuille={feuille}
                            surFeuille={analyserClasseur}
                            anneeLibelle={anneeActive?.libelle}
                            occupe={occupe}
                            modifiable={modifiable}
                            surImporter={importer}
                            surAnnuler={recommencer}
                            formations={formations}
                            parcours={parcours}
                            surAssocier={associerCode}
                            surModifier={modifierCode}
                        />
                    )}

                    {/* ── Étape 3 : le bilan ── */}
                    {etape === 'bilan' && bilan && (
                        <>
                            <Bilan bilan={bilan} />
                            <div className="etu-import-actions">
                                <button type="button" className="etu-btn etu-btn-neutre" onClick={recommencer}>
                                    <i className="fa-solid fa-rotate-left" />{' '}
                                    {mode === 'coller' ? 'Coller un autre tableau' : 'Importer un autre fichier'}
                                </button>
                            </div>
                        </>
                    )}

                    {/* Hors flux d'import : le bilan du dernier import, comme la maquette. */}
                    {etape === 'depot' && bilan && <Bilan bilan={bilan} dernier />}

                </div>
            </div>

            {/* ── Historique ── */}
            <div className="etu-carte">
                <div className="etu-carte-entete">
                    <i className="fa-solid fa-clock-rotate-left" /> Historique APOGEE
                </div>
                {historique.length === 0 ? (
                    <div className="etu-table-vide">Aucun import APOGEE pour ce département</div>
                ) : (
                    <table className="etu-table etu-table-histo">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Mode</th>
                                {/* La maquette met des emoji dans ces trois en-têtes
                                    (✅ 🔄 ❌). Règle 6, écart E1 : FontAwesome, avec le
                                    sens en infobulle puisqu'un glyphe seul ne se lit pas. */}
                                <th className="etu-col-nb" title="Créés">
                                    <i className="fa-solid fa-user-plus" />
                                </th>
                                <th className="etu-col-nb" title="Mis à jour">
                                    <i className="fa-solid fa-rotate" />
                                </th>
                                <th className="etu-col-nb" title="Erreurs">
                                    <i className="fa-solid fa-triangle-exclamation" />
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {historique.map((h) => (
                                <tr
                                    key={h.id}
                                    className={bilan?.import_id === h.id ? 'etu-ligne-active' : ''}
                                    onClick={() => { setBilan(bilanDepuisJournal(h)); setEtape('depot'); }}
                                    title="Afficher le bilan de cet import"
                                >
                                    <td>
                                        <div>{dateHeure(h.created_at)}</div>
                                        <div className="etu-histo-fichier">
                                            <i className={'fa-solid ' + (h.mode === 'coller' ? 'fa-clipboard' : 'fa-file-excel')} />
                                            {h.fichier_nom || 'collage'}
                                            {h.auteur ? ' · ' + h.auteur : ''}
                                        </div>
                                    </td>
                                    <td className="etu-histo-mode">{h.mode}</td>
                                    <td className="etu-col-nb etu-nb-crees">{h.nb_crees}</td>
                                    <td className="etu-col-nb etu-nb-majs">{h.nb_mis_a_jour}</td>
                                    <td className="etu-col-nb etu-nb-erreurs">{h.nb_erreurs}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
            </div>
    );
}

// ═════════════════════════════════════════════════════════════════════════════
// LA PRÉVISUALISATION — les deux temps
// ═════════════════════════════════════════════════════════════════════════════
export const LIBELLES_REGIME = { initiale: 'Initiale', apprentis: 'Apprentis', fc: 'FC' };

/**
 * Lot E.3 — l'étape ② « Les codes du fichier » (maquette etudiants_maquette_v5.html, révision « mêmes étapes que
 * SCODOC », validée le 2026-10-28). Un code connu : son groupe, son régime, son parcours (choisissable s'il manque).
 * Un code nouveau : il s'associe ICI et rejoint le mapping du Paramétrage — ses lignes attendent jusque-là, les
 * autres s'importent.
 */
function CodesDuFichier({ codes, formations, parcours, modifiable, occupe, surAssocier, surModifier }) {
    const [choix, setChoix] = useState({});
    const cle = (c) => c.code_etape + '/' + c.version_etape;
    const regle = (c, champ, v) => setChoix((x) => ({ ...x, [cle(c)]: { ...(x[cle(c)] || {}), [champ]: v } }));
    const [edition, setEdition] = useState(null);   // la clé du code connu en cours de modification
    const enAttente = codes.filter((c) => !c.mapping);
    const ordonnes = [...enAttente, ...codes.filter((c) => c.mapping)];
    // Les trois menus, communs à l'association d'un code nouveau et à la modification d'un code connu
    const menus = (c, ch) => (<>
        <td>
            <select value={ch.sous_niveau_id || ''} disabled={!modifiable || occupe} aria-label={`Groupe de ${cle(c)}`} onChange={(e) => regle(c, 'sous_niveau_id', e.target.value)}>
                <option value="">— à associer —</option>
                {formations.map((f) => <option key={f.id} value={f.id}>{f.nom}</option>)}
            </select>
        </td>
        <td>
            <select value={ch.regime_inscription || ''} disabled={!modifiable || occupe} aria-label={`Régime de ${cle(c)}`} onChange={(e) => regle(c, 'regime_inscription', e.target.value)}>
                <option value="">régime…</option>
                {Object.entries(LIBELLES_REGIME).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
        </td>
        <td>
            <select value={ch.parcours_code || ''} disabled={!modifiable || occupe} aria-label={`Parcours de ${cle(c)}`} onChange={(e) => regle(c, 'parcours_code', e.target.value)}>
                <option value="">— tronc commun</option>
                {parcours.map((p) => <option key={p.id} value={p.code}>{p.code}</option>)}
                {/* un code déjà choisi mais absent du Programme de l'année reste visible */}
                {ch.parcours_code && !parcours.some((p) => p.code === ch.parcours_code) && <option value={ch.parcours_code}>{ch.parcours_code}</option>}
            </select>
        </td>
    </>);
    const ouvrirEdition = (c) => {
        setChoix((x) => ({ ...x, [cle(c)]: { sous_niveau_id: String(c.mapping.sous_niveau_id), regime_inscription: c.mapping.regime_inscription, parcours_code: c.mapping.parcours_code || '' } }));
        setEdition(cle(c));
    };
    return (
        <div className="etu-codes">
            <div className="etu-etape-titre">
                <span className="etu-etape-num">2</span> <strong>Les codes du fichier</strong>
                <span className="etu-aide"> — ce qui est connu est prérempli ; un code nouveau s'associe ici et rejoint le mapping du Paramétrage.</span>
            </div>
            <table className="etu-table etu-table-codes">
                <thead><tr><th>Code étape / version</th><th className="etu-col-nb">Étud.</th><th>→ Groupe</th><th>Régime</th><th>Parcours</th><th /></tr></thead>
                <tbody>
                    {ordonnes.map((c) => {
                        const ch = choix[cle(c)] || {};
                        const pret = ch.sous_niveau_id && ch.regime_inscription;
                        if (c.mapping && edition === cle(c)) {
                            // « Modifier » : la ligne passe en édition, préremplie ; Enregistrer corrige le mapping
                            return (
                                <tr key={cle(c)} className="etu-code-edition">
                                    <td className="etu-mono">{c.code_etape} / {c.version_etape}</td>
                                    <td className="etu-col-nb">{c.nb_lignes}</td>
                                    {menus(c, ch)}
                                    <td className="etu-codes-actions">
                                        <button type="button" className="etu-btn etu-btn-apogee etu-btn-petit" disabled={!pret || occupe || !modifiable}
                                                title="Corriger cette association et refaire l'aperçu"
                                                onClick={() => { setEdition(null); surModifier(c, ch); }}>
                                            <i className="fa-solid fa-check" /> Enregistrer
                                        </button>
                                        {/* une croix et non « Annuler » : la ligne doit tenir dans la colonne (relu à l'image) */}
                                        <button type="button" className="etu-btn etu-btn-neutre etu-btn-petit" disabled={occupe} onClick={() => setEdition(null)}
                                                title="Annuler la modification" aria-label="Annuler la modification">
                                            <i className="fa-solid fa-xmark" />
                                        </button>
                                    </td>
                                </tr>
                            );
                        }
                        if (c.mapping) {
                            return (
                                <tr key={cle(c)}>
                                    <td className="etu-mono">{c.code_etape} / {c.version_etape}</td>
                                    <td className="etu-col-nb">{c.nb_lignes}</td>
                                    <td><span className="etu-badge-exemple">{c.mapping.formation_nom}</span> <i className="fa-solid fa-memory etu-memorise" title="Mémorisé dans le mapping du Paramétrage" /></td>
                                    <td><span className={'etu-tag-regime etu-regime-' + c.mapping.regime_inscription}>{LIBELLES_REGIME[c.mapping.regime_inscription] || c.mapping.regime_inscription}</span></td>
                                    <td>{c.mapping.parcours_code ? <strong>{c.mapping.parcours_code}</strong> : <span className="etu-aide">— tronc commun</span>}</td>
                                    <td>
                                        {modifiable && (
                                            <button type="button" className="etu-btn etu-btn-neutre etu-btn-petit" disabled={occupe || edition !== null}
                                                    title="Corriger le groupe, le régime ou le parcours de ce code (il est corrigé aussi dans le Paramétrage)"
                                                    onClick={() => ouvrirEdition(c)}>
                                                <i className="fa-solid fa-pen" /> Modifier
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        }
                        return (
                            <tr key={cle(c)} className="etu-code-attente">
                                <td className="etu-mono">{c.code_etape} / {c.version_etape}</td>
                                <td className="etu-col-nb">{c.nb_lignes}</td>
                                {menus(c, ch)}
                                <td>
                                    <button type="button" className="etu-btn etu-btn-apogee etu-btn-petit" disabled={!pret || occupe || !modifiable}
                                            title={pret ? 'Enregistrer cette association et refaire l\'aperçu' : 'Choisissez le groupe et le régime'}
                                            onClick={() => surAssocier(c, ch)}>
                                        <i className="fa-solid fa-check" /> Associer
                                    </button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {enAttente.length > 0 && (
                <div className="etu-codes-attente">
                    <i className="fa-solid fa-triangle-exclamation" />{' '}
                    {enAttente.length} code{enAttente.length > 1 ? 's restent' : ' reste'} à associer
                    ({enAttente.map((c) => `${c.code_etape} / ${c.version_etape}, ${c.nb_lignes} étudiant${c.nb_lignes > 1 ? 's' : ''}`).join(' ; ')}) :
                    {' '}leurs lignes attendent ; les autres s'importent.
                </div>
            )}
            {parcours.length === 0 && (
                <div className="etu-aide etu-aide-bloc">
                    <i className="fa-solid fa-circle-info" /> Aucun parcours pour cette année dans le Programme : ils se créent dans
                    {' '}<strong>Programme formation → Programme national → Semestres</strong>.
                </div>
            )}
        </div>
    );
}

/** Lot E.3 — les écarts : une valeur du fichier contredit la base. Listés, JAMAIS écrits ; on corrige sur la fiche. */
export function Ecarts({ ecarts }) {   // réutilisé par ImportScodocEtudiants (lot E.3)
    if (!ecarts || !ecarts.length) return null;
    return (
        <div className="etu-ecarts">
            {ecarts.map((x) => (
                <div className="etu-ecart" key={x.ligne + '-' + x.code_etudiant}>
                    <i className="fa-solid fa-triangle-exclamation" />{' '}
                    <strong>Écart, non écrit</strong> — {x.identite} :{' '}
                    {x.champs.map((c, k) => (
                        <span key={k}>{k > 0 ? ' ; ' : ''}{c.libelle} <strong>{lisible(c.base)}</strong> en base, <strong>{lisible(c.fichier)}</strong> dans le fichier</span>
                    ))}.
                </div>
            ))}
        </div>
    );
}

function Previsualisation({
    analyse, apercu, mode, fichierNom, feuilles, feuille, surFeuille,
    anneeLibelle, occupe, modifiable, surImporter, surAnnuler,
    formations = [], parcours = [], surAssocier, surModifier
}) {
    const enCollage = mode === 'coller';
    const [detailsOuverts, setDetailsOuverts] = useState(false);
    const apparieees = analyse.colonnes.filter((c) => c.trouvee);
    const aImporter = apercu
        ? apercu.nouveaux.length + apercu.modifies.length + apercu.identiques.length
        : 0;
    const verrouilles = apercu?.verrouilles || [];
    const rapproches = apercu?.rapproches || [];

    /* ── L'ORDRE DE CET ÉCRAN VIENT DE LA RELECTURE À L'IMAGE ──────────────────
       Première version : le tableau des dix-huit colonnes venait en premier, et
       poussait les compteurs, les alertes ET le bouton « Importer » hors de l'écran.
       L'action principale était inatteignable sans défiler, et l'historique en
       dessous n'était plus qu'un liseré.

       Donc : le bilan de ce qui va se passer et le bouton d'abord, la vérification
       colonne par colonne ensuite — visible sans clic, comme le §0.8 l'exige, mais
       dans un cadre à hauteur plafonnée qui défile pour lui seul. */

    return (
        <div className="etu-apercu">

            {/* En-tête : le fichier, la feuille, l'année visée */}
            <div className="etu-apercu-entete">
                <i className={'fa-solid ' + (enCollage ? 'fa-clipboard' : 'fa-eye')} />
                {/* En collage il n'y a pas de nom de fichier : on nomme la source pour
                    que l'en-tête ne soit pas vide, et il n'y a pas de feuille à choisir. */}
                <span><strong>{enCollage ? 'Collage depuis Excel' : fichierNom}</strong></span>
                {!enCollage && feuilles.length > 1 && (
                    <>
                        <span className="etu-apercu-sep">·</span>
                        <span>feuille</span>
                        <select value={feuille} onChange={(e) => surFeuille(e.target.value)}>
                            {feuilles.map((f) => <option key={f} value={f}>{f}</option>)}
                        </select>
                    </>
                )}
                <span className="etu-apercu-sep">·</span>
                <span>{analyse.nbLignesUtiles} ligne{analyse.nbLignesUtiles > 1 ? 's' : ''} de données</span>
                {anneeLibelle && (
                    <span className="etu-apercu-annee">
                        <i className="fa-solid fa-calendar" /> {anneeLibelle}
                    </span>
                )}
            </div>

            {analyse.bloquant && (
                <div className="etu-statut etu-statut-ko etu-statut-bloc">
                    <i className="fa-solid fa-circle-exclamation" />
                    <span>{analyse.bloquant}</span>
                </div>
            )}

            {/* ── TEMPS 2 : ce que l'import ferait, et le bouton ── */}
            {occupe && !apercu && (
                <div className="etu-aide etu-aide-bloc">
                    <i className="fa-solid fa-spinner fa-spin" /> Comparaison avec la base…
                </div>
            )}

            {apercu && (
                <>
                    {/* Lot E.3 : ② les codes, associés sur place ; ③ l'aperçu commun aux deux imports */}
                    {(apercu.codes || []).length > 0 && (
                        <CodesDuFichier codes={apercu.codes} formations={formations} parcours={parcours} modifiable={modifiable}
                                        occupe={occupe} surAssocier={surAssocier} surModifier={surModifier} />
                    )}
                    <div className="etu-etape-titre">
                        <span className="etu-etape-num">3</span> <strong>Ce que l'import fera</strong>
                        <span className="etu-aide"> — rien n'est écrit avant « Importer »</span>
                    </div>
                    <div className="etu-compteurs">
                        <div className="etu-compteur">
                            <div className="etu-compteur-nb etu-nb-crees">{apercu.nouveaux.length}</div>
                            <div className="etu-compteur-label">Créés</div>
                        </div>
                        <div className="etu-compteur">
                            <div className="etu-compteur-nb etu-nb-majs">{apercu.modifies.length}</div>
                            <div className="etu-compteur-label">Complétés</div>
                        </div>
                        <div className="etu-compteur">
                            <div className="etu-compteur-nb etu-nb-identiques">{apercu.identiques.length}</div>
                            <div className="etu-compteur-label">Inchangés</div>
                        </div>
                        <div className="etu-compteur">
                            <div className="etu-compteur-nb etu-nb-ecarts">{(apercu.ecarts || []).length}</div>
                            <div className="etu-compteur-label">Écarts signalés</div>
                        </div>
                        <div className="etu-compteur">
                            {/* Les DEUX sources de refus : celles que le serveur voit (code
                                non mappé, INE contradictoire) ET celles que le navigateur a
                                écartées avant d'envoyer (clé absente, doublon dans le
                                fichier, identité incomplète). N'afficher que les premières
                                donnait « 0 refusés » sur un fichier dont une ligne était
                                pourtant écartée — trouvé en jouant le cas `2_doublon_cle`. */}
                            <div className="etu-compteur-nb etu-nb-erreurs">
                                {apercu.refuses.length + analyse.refus.length}
                            </div>
                            <div className="etu-compteur-label">En attente / refusés</div>
                        </div>
                    </div>
                    <Ecarts ecarts={apercu.ecarts} />
                    {apercu.modifies.length > 0 && (
                        <div className="etu-aide etu-aide-bloc">
                            <i className="fa-solid fa-circle-info" /> « Complétés » : des champs <strong>vides</strong> remplis (mention du bac,
                            redoublant, classement, code d'étape…), jamais une valeur remplacée.
                        </div>
                    )}

                    <div className="etu-import-actions">
                        <button
                            type="button"
                            className="etu-btn etu-btn-apogee"
                            disabled={!modifiable || occupe || !!analyse.bloquant || aImporter === 0}
                            onClick={surImporter}
                        >
                            <i className="fa-solid fa-file-import" />{' '}
                            Importer {aImporter} ligne{aImporter > 1 ? 's' : ''}
                        </button>
                        <button type="button" className="etu-btn etu-btn-neutre" onClick={surAnnuler} disabled={occupe}>
                            <i className="fa-solid fa-xmark" /> Annuler
                        </button>
                        {(apercu.refuses.length > 0 || analyse.refus.length > 0
                          || apercu.modifies.length > 0 || verrouilles.length > 0
                          || analyse.avertissements.length > 0) && (
                            <button
                                type="button"
                                className="etu-btn etu-btn-neutre"
                                onClick={() => setDetailsOuverts((v) => !v)}
                            >
                                <i className={'fa-solid ' + (detailsOuverts ? 'fa-chevron-up' : 'fa-chevron-down')} />
                                {detailsOuverts ? ' Masquer le détail' : ' Détail ligne à ligne'}
                            </button>
                        )}
                    </div>

                    {/* Lot E.3 : l'ancien bandeau « … à associer dans l'onglet Paramétrage » a disparu — les codes
                        s'associent dans l'étape ② ci-dessus, sans quitter l'import. */}

                    {/* Les cadenas qui vont jouer. À part, et non dans la liste des
                        modifiés : un étudiant dont le SEUL écart porte sur un champ
                        verrouillé n'a rien à changer, il compte donc comme inchangé —
                        et sans ce bandeau, l'information que le cadenas a joué
                        disparaissait avec lui (trouvé au banc). */}
                    {verrouilles.length > 0 && (
                        <div className="etu-alerte etu-alerte-info">
                            <i className="fa-solid fa-lock" />
                            <span>
                                {verrouilles.length} étudiant{verrouilles.length > 1 ? 's ont' : ' a'} au
                                moins un champ <strong>verrouillé</strong> : le fichier propose une autre
                                valeur, elle ne sera <strong>pas</strong> écrite. Le détail ligne à ligne
                                nomme chaque champ concerné.
                            </span>
                        </div>
                    )}

                    {/* Le rapprochement par l'INE (lot 1.2 ter). À dire AVANT d'écrire :
                        l'import va compléter des fiches qui existaient déjà — celles de
                        l'import PRONOTE, qui portent l'INE mais aucun numéro étudiant.
                        Sans ce bandeau, l'utilisateur croirait à des créations. */}
                    {rapproches.length > 0 && (
                        <div className="etu-alerte etu-alerte-info">
                            <i className="fa-solid fa-link" />
                            <span>
                                {rapproches.length} fiche{rapproches.length > 1 ? 's' : ''} déjà
                                présente{rapproches.length > 1 ? 's' : ''} sans numéro étudiant
                                {' '}<strong>seront rattachées par leur INE</strong> et recevront leur
                                numéro — elles ne seront pas dupliquées. Ex. {rapproches[0].identite},
                                INE {rapproches[0].ine}.
                            </span>
                        </div>
                    )}

                    {apercu.regimesDivergents.length > 0 && (
                        <div className="etu-alerte etu-alerte-info">
                            <i className="fa-solid fa-circle-info" />
                            <span>
                                {apercu.regimesDivergents.length} ligne
                                {apercu.regimesDivergents.length > 1 ? 's' : ''} où le régime du fichier
                                diffère de celui du mapping — <strong>le mapping fait foi</strong>.
                                {' '}Ex. {apercu.regimesDivergents[0].couple} : fichier «&nbsp;
                                {apercu.regimesDivergents[0].fichier}&nbsp;», mapping «&nbsp;
                                {apercu.regimesDivergents[0].mapping}&nbsp;».
                            </span>
                        </div>
                    )}

                    {detailsOuverts && (
                        <div className="etu-detail-lignes">
                            {/* Les refus du serveur ET ceux du navigateur, dans la même
                                liste : pour l'utilisateur, une ligne écartée est une ligne
                                écartée — peu importe qui l'a vue. */}
                            {regrouperRefus([...analyse.refus, ...apercu.refuses]).map((r, i) => (
                                <div className="etu-detail-ligne etu-detail-refus" key={'r' + i}>
                                    <i className="fa-solid fa-circle-xmark" />
                                    <span className="etu-detail-num">{r.reperes}</span>
                                    <span>{r.message}</span>
                                </div>
                            ))}
                            {analyse.avertissements.map((a, i) => (
                                <div className="etu-detail-ligne etu-detail-avert" key={'a' + i}>
                                    <i className="fa-solid fa-triangle-exclamation" />
                                    <span className="etu-detail-num">L.{a.ligne}</span>
                                    <span>{a.message}</span>
                                </div>
                            ))}
                            {/* Les modifiés, puis les lignes dont TOUS les écarts sont
                                verrouillés — celles-là comptent comme inchangées, il faut
                                donc les ajouter ici sans les compter deux fois. */}
                            {[...apercu.modifies,
                              ...verrouilles.filter((v) => v.changements.length === 0)
                             ].map((m, i) => (
                                <div className="etu-detail-ligne etu-detail-modif" key={'m' + i}>
                                    <i className="fa-solid fa-rotate" />
                                    <span className="etu-detail-num">L.{m.ligne}</span>
                                    <span>
                                        <strong>{m.identite}</strong>
                                        <ul className="etu-detail-changements">
                                            {m.changements.map((c, k) => (
                                                <li key={k}>
                                                    {nomChamp(c.champ)} : <em>{lisible(c.avant)}</em>
                                                    {' → '}<strong>{lisible(c.apres)}</strong>
                                                </li>
                                            ))}
                                            {m.proteges.map((p, k) => (
                                                <li key={'p' + k} className="etu-detail-protege">
                                                    <i className="fa-solid fa-lock" /> {nomChamp(p.champ)} :
                                                    verrouillé, conservé à <strong>{lisible(p.actuel)}</strong>
                                                    {' '}(le fichier dit {lisible(p.propose)})
                                                </li>
                                            ))}
                                        </ul>
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}

            {/* ── TEMPS 1 : la vérification colonne par colonne (§0.8) ── */}
            <div className="etu-apercu-titre">
                <i className="fa-solid fa-table-columns" /> Colonnes reconnues
                <span className="etu-apercu-compte">{apparieees.length} sur {analyse.entetes.length}</span>
            </div>

            {/* Le classeur déclare plus de lignes qu'il n'en porte : c'est ce qui fait
                croire à 187 étudiants là où il y en a 147. On le dit, plutôt que de
                laisser un doute sur les lignes manquantes. */}
            {analyse.nbLignesDeclarees > analyse.nbLignesUtiles && (
                <div className="etu-aide etu-aide-bloc">
                    <i className="fa-solid fa-circle-info" />{' '}
                    La feuille déclare <strong>{analyse.nbLignesDeclarees}</strong> lignes et n'en
                    porte que <strong>{analyse.nbLignesUtiles}</strong> : les lignes vides déclarées
                    au-delà des données sont ignorées.
                </div>
            )}

            <div className="etu-table-cadre etu-table-cadre-apogee etu-cadre-colonnes">
                <table className="etu-table etu-table-apogee">
                    <thead>
                        <tr>
                            <th>Colonne du fichier</th>
                            <th>→ Champ</th>
                            <th className="etu-col-taux">Conforme</th>
                            <th>Ce qui sera écrit</th>
                        </tr>
                    </thead>
                    <tbody>
                        {apparieees.map((c) => (
                            <tr key={c.cle} className={c.refusee ? 'etu-ligne-refusee' : ''}>
                                <td className="etu-mono">
                                    {/* Trois états, et non deux : une pastille VERTE sur une
                                        colonne sans aucune valeur dirait « tout va bien » là où
                                        il n'y a rien à vérifier (relu à l'image). */}
                                    {c.refusee
                                        ? <i className="fa-solid fa-circle-xmark etu-refus" title="colonne refusée" />
                                        : c.nbNonVides === 0
                                            ? <i className="fa-solid fa-circle-minus etu-neutre" title="colonne vide : rien à vérifier" />
                                            : <i className="fa-solid fa-circle-check etu-conforme" title="forme conforme" />}
                                    {' '}{c.intituleSource}
                                    {c.role === 'cle' && <span className="etu-tag-cle">clé</span>}
                                </td>
                                <td>
                                    {c.cible
                                        ? <span className="etu-badge-champ">{c.cible}</span>
                                        : <span className="etu-aide">contrôle seul</span>}
                                </td>
                                <td className="etu-col-taux">
                                    {pourcent(c.taux)}
                                    <span className="etu-aide"> · {c.nbNonVides}</span>
                                    {c.refusee && (
                                        <div className="etu-refus-motif">
                                            refusée — attendu&nbsp;: {c.forme}
                                        </div>
                                    )}
                                </td>
                                <td className="etu-apercu-exemples">
                                    {c.exemples.length
                                        ? c.exemples.map((ex, k) => (
                                            <span key={k}>
                                                {k > 0 && ' · '}
                                                {/* Ce qui sera ÉCRIT, la source en infobulle quand la
                                                    conversion la change : c'est la valeur en base que
                                                    l'utilisateur doit pouvoir vérifier. Sans cela, la
                                                    colonne des dates n'affichait que des serials Excel
                                                    (« 39043 ») et celle des portables leurs six formes
                                                    brutes — relu à l'image. */}
                                                <span
                                                    className={ex.converti ? 'etu-exemple-converti' : ''}
                                                    title={ex.converti ? `dans le fichier : ${ex.source}` : undefined}
                                                >
                                                    {ex.ecrit || ex.source}
                                                </span>
                                            </span>
                                        ))
                                        : <span className="etu-aide">vide</span>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {analyse.colonnesRefusees.length > 0 && (
                <div className="etu-alerte etu-alerte-warning">
                    <i className="fa-solid fa-triangle-exclamation" />
                    <span>
                        {analyse.colonnesRefusees.length} colonne
                        {analyse.colonnesRefusees.length > 1 ? 's' : ''} refusée
                        {analyse.colonnesRefusees.length > 1 ? 's' : ''} : leur contenu ne correspond
                        pas à leur intitulé. <strong>Rien n'est deviné</strong> — ces champs ne seront
                        pas écrits, le reste de la ligne passe.
                    </span>
                </div>
            )}

            {/* Ce que l'import ne lit délibérément pas : le dire ici évite la question
                « et mon adresse, elle est passée où ? » (§0.6, liste blanche). */}
            <details className="etu-details">
                <summary>
                    <i className="fa-solid fa-shield-halved" />{' '}
                    {analyse.nonLues.length + analyse.inconnues.length} colonne(s) non lues, et pourquoi
                </summary>
                <ul className="etu-liste-non-lues">
                    {[...analyse.nonLues, ...analyse.inconnues].map((c, i) => (
                        <li key={i}><span className="etu-mono">{c.intitule}</span> — {c.motif}</li>
                    ))}
                </ul>
            </details>

            {analyse.bloquant && !apercu && (
                <div className="etu-import-actions">
                    <button type="button" className="etu-btn etu-btn-neutre" onClick={surAnnuler}>
                        <i className="fa-solid fa-rotate-left" />{' '}
                        {enCollage ? 'Revenir à la zone de collage' : 'Choisir un autre fichier'}
                    </button>
                </div>
            )}
        </div>
    );
}


// ═════════════════════════════════════════════════════════════════════════════
// LE BILAN — trois compteurs, comme la maquette
// ═════════════════════════════════════════════════════════════════════════════
function Bilan({ bilan, dernier }) {
    const anomalies = bilan.anomalies || [];
    // Deux motifs ne sont PAS des erreurs et ne doivent pas s'afficher en rouge : le
    // régime divergent (le mapping fait foi, la ligne entre) et le rapprochement par
    // l'INE (une fiche existante a reçu son numéro). Miroir exact de
    // `MOTIFS_NON_BLOQUANTS` côté serveur — si les deux listes divergeaient, l'écran
    // compterait des erreurs que le journal ne compte pas.
    // Lot E.1/E.3 : un ÉCART (jamais écrit), un parcours absent du Programme et un code EN ATTENTE ne sont pas des erreurs.
    const MOTIFS_AVERTISSEMENT = ['regime_divergent', 'rapprochement_ine', 'ecart', 'parcours_absent', 'code_non_mappe'];
    const bloquantes = anomalies.filter((a) => !MOTIFS_AVERTISSEMENT.includes(a.motif));
    const avertissements = anomalies.filter((a) => MOTIFS_AVERTISSEMENT.includes(a.motif));

    return (
        <div className="etu-bilan">
            <div className={'etu-bilan-entete' + (bilan.nb_erreurs ? ' avec-erreurs' : '')}>
                <i className={'fa-solid ' + (bilan.nb_erreurs ? 'fa-circle-exclamation' : 'fa-circle-check')} />
                <span>
                    {dernier && <span className="etu-bilan-dernier">Dernier import — </span>}
                    {dateHeure(bilan.date_import)}
                    {bilan.fichier_nom ? ' — ' + bilan.fichier_nom : ' — collage'}
                </span>
            </div>

            <div className="etu-compteurs">
                <div className="etu-compteur">
                    <div className="etu-compteur-nb etu-nb-crees">{bilan.nb_crees}</div>
                    <div className="etu-compteur-label">Créés</div>
                </div>
                <div className="etu-compteur">
                    <div className="etu-compteur-nb etu-nb-majs">{bilan.nb_mis_a_jour}</div>
                    <div className="etu-compteur-label">Complétés</div>
                </div>
                {/* Lot E.1 : les imports d'avant ne les comptaient pas (journal à 0) — affichés seulement s'ils ont joué */}
                {bilan.nb_inchanges > 0 && (
                    <div className="etu-compteur">
                        <div className="etu-compteur-nb etu-nb-identiques">{bilan.nb_inchanges}</div>
                        <div className="etu-compteur-label">Inchangés</div>
                    </div>
                )}
                {bilan.nb_ecarts > 0 && (
                    <div className="etu-compteur">
                        <div className="etu-compteur-nb etu-nb-ecarts">{bilan.nb_ecarts}</div>
                        <div className="etu-compteur-label">Écarts signalés</div>
                    </div>
                )}
                {/* Compteur affiché SEULEMENT s'il a joué : sur une base sans fiches
                    PRONOTE il vaut toujours zéro, et une colonne à zéro qui ne bouge
                    jamais est du bruit. */}
                {bilan.nb_rapproches > 0 && (
                    <div className="etu-compteur">
                        <div className="etu-compteur-nb etu-nb-rapproches">{bilan.nb_rapproches}</div>
                        <div className="etu-compteur-label">Rattachés</div>
                    </div>
                )}
                <div className="etu-compteur">
                    <div className="etu-compteur-nb etu-nb-erreurs">{bilan.nb_erreurs}</div>
                    <div className="etu-compteur-label">Erreurs</div>
                </div>
            </div>

            {/* Toutes les anomalies, dans une zone qui défile : la maquette n'en montre
                que deux pour trois erreurs, mais une erreur cachée est une erreur qu'on
                ne corrige pas. Le compte est rappelé au-dessus. */}
            {(bloquantes.length > 0 || avertissements.length > 0) && (
                <div className="etu-bilan-anomalies">
                    {/* Même regroupement que la prévisualisation : sur le fichier réel,
                        77 refus pour 13 codes donnaient 77 lignes quasi identiques. */}
                    {regrouperRefus(bloquantes).map((a, i) => (
                        <div className="etu-detail-ligne etu-detail-refus" key={'b' + i}>
                            <i className="fa-solid fa-triangle-exclamation" />
                            <span className="etu-detail-num">{a.reperes}</span>
                            <span>{a.message}</span>
                        </div>
                    ))}
                    {avertissements.map((a, i) => (
                        <div className="etu-detail-ligne etu-detail-avert" key={'w' + i}>
                            <i className="fa-solid fa-circle-info" />
                            <span className="etu-detail-num">L.{a.ligne}</span>
                            <span>{a.message}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

/**
 * Une ligne du journal relue comme un bilan. C'est ce qui permet à la maquette
 * d'afficher un bilan en permanence, et au clic sur l'historique de rejouer un import
 * passé sans requête supplémentaire : `detail_erreurs` est déjà là.
 */
function bilanDepuisJournal(ligne) {
    return {
        import_id: ligne.id,
        date_import: ligne.created_at,
        fichier_nom: ligne.fichier_nom,
        mode: ligne.mode,
        nb_crees: ligne.nb_crees,
        nb_mis_a_jour: ligne.nb_mis_a_jour,
        // Lot E.1 : `nb_ignores` compte désormais les inchangés de l'import Apogée ; `nb_ecarts` les contradictions
        nb_inchanges: ligne.nb_ignores,
        nb_ecarts: ligne.nb_ecarts,
        nb_erreurs: ligne.nb_erreurs,
        anomalies: Array.isArray(ligne.detail_erreurs) ? ligne.detail_erreurs : []
    };
}

export default ImportApogee;
